import { appendBrandIds } from '@/lib/brand-filter-params';
import type { Order } from './orders-client';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
}

async function api<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const { method = 'GET', body } = options;
  const url = `${API_BASE_URL}${endpoint}`;

  const fetchOptions: RequestInit = {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  };

  if (body && method !== 'GET') {
    fetchOptions.body = JSON.stringify(body);
  }

  const response = await fetch(url, fetchOptions);

  if (response.status === 401 || response.status === 403) {
    window.dispatchEvent(new CustomEvent('auth:unauthorized', { detail: { status: response.status } }));
    throw new Error(`Unauthorized (${response.status})`);
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`API error (${response.status}): ${errorText}`);
  }

  return response.json();
}

export interface CustomerAddress {
  id: string;
  raw: string;
  source?: string;
  customerInput?: string;
  coordinates?: { lat: number; lng: number };
  resolvedAddress?: string;
  street?: string;
  doorNumber?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  apartment?: string;
  /** The customer's note for the driver on this address ("portón verde, timbre 3"). */
  instructions?: string;
  geocodeQuality?: string;
  label?: string;
  lastUsedAt: string;
}

export interface Customer {
  id: string;
  phone: string;
  name?: string;
  addresses?: CustomerAddress[];
  createdAt: string;
  updatedAt: string;
}

interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasMore: boolean;
}

export interface CustomerProfile {
  customer: Customer;
  orders: Order[];
  pagination: PaginationMeta;
}

/**
 * One row of `GET /customers/search`: a customer with an order at a brand in scope, matched by phone
 * digits or name. Small on purpose — what a picker row shows and what picking it fills in.
 */
export interface CustomerSearchHit {
  id: string;
  phone: string;
  name?: string;
  /** Their newest order with the brands searched (ISO). */
  lastOrderAt: string;
  /** At most the 4 most recently used delivery addresses of those orders, newest first. */
  addresses: CustomerAddress[];
}

export const customersClient = {
  async getProfile(
    phone: string,
    params?: { page?: number; limit?: number; brandIds?: string[] },
  ): Promise<ApiResponse<CustomerProfile>> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    appendBrandIds(searchParams, params?.brandIds);
    const query = searchParams.toString();
    return api<ApiResponse<CustomerProfile>>(
      `/customers/phone/${encodeURIComponent(phone)}/profile${query ? `?${query}` : ''}`,
    );
  },

  /**
   * The brand's customers whose phone holds the digits of `q`, or whose name holds `q`; newest
   * orderer first. The server answers `[]` for a `q` under 2 characters; `limit` defaults to 8 (max
   * 10). Aggregate sessions narrow it with `brandIds` (a brand outside the session is a 400).
   */
  async search(q: string, params?: { limit?: number; brandIds?: string[] }): Promise<ApiResponse<CustomerSearchHit[]>> {
    const searchParams = new URLSearchParams({ q });
    if (params?.limit) searchParams.set('limit', String(params.limit));
    appendBrandIds(searchParams, params?.brandIds);
    return api<ApiResponse<CustomerSearchHit[]>>(`/customers/search?${searchParams.toString()}`);
  },
};
